import { useCallback, useMemo, useRef } from "react";

// Diagnosis commands that make the server re-plan (and often narrate) before it
// answers. The server ends each with tse:idle (v5/hooks.py); a command refused
// at the gate gets only a command_result. teach / learning_toggle / dismiss
// re-plan nothing and keep their own pending marks.
export const REPLAN_COMMANDS = new Set([
  "focus_lod",
  "override_cause",
  "undo_override",
  "switch_to_automatic",
]);

const STATUS_APPLIED = "applied";

/**
 * The thinking state for a re-planning diagnosis command.
 *
 * Raised only when no turn is already streaming, so settling the command can
 * never cut a running turn short. Ended by tse:idle / tse:error through the
 * caller's normal teardown (`release`, also on disconnect / stop / clear), or
 * here by a command_result other than applied for the SAME command — a
 * rejected command is followed by no idle at all, and a late result for
 * another command must not end this one.
 */
export default function useCommandBusy({ isStreamingRef, setIsStreaming }) {
  const begunRef = useRef(null);

  const begin = useCallback((action) => {
    if (!REPLAN_COMMANDS.has(action) || isStreamingRef.current) return;
    begunRef.current = action;
    setIsStreaming(true);
  }, [isStreamingRef, setIsStreaming]);

  const settle = useCallback((result) => {
    if (!begunRef.current || result?.command !== begunRef.current || result?.status === STATUS_APPLIED) return;
    begunRef.current = null;
    setIsStreaming(false);
  }, [setIsStreaming]);

  const release = useCallback(() => {
    begunRef.current = null;
  }, []);

  return useMemo(() => ({ begin, settle, release }), [begin, settle, release]);
}
